import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, assetUrl } from "@/lib/api";
import { Plus, Eye, EyeOff, Trash2, Star, FileText, Edit3, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function AdminArticles() {
  const [list, setList] = useState([]);
  const [demoCount, setDemoCount] = useState(0);
  const [demoBusy, setDemoBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/articles");
    setList(data);
    setDemoCount(data.filter((a) => a.is_demo).length);
  };
  useEffect(() => { load(); }, []);

  const seedDemo = async () => {
    if (!window.confirm("Load 15 sample (demo) articles to populate the journal? They are clearly tagged and can be removed all at once later.")) return;
    setDemoBusy(true);
    try {
      const { data } = await api.post("/admin/demo/seed");
      toast.success(data.created ? `Loaded ${data.created} demo articles.` : (data.message || "Demo articles already present."));
      await load();
    } catch (e) {
      toast.error("Could not load demo articles: " + (e.response?.data?.detail || e.message));
    } finally { setDemoBusy(false); }
  };

  const purgeDemo = async () => {
    if (!window.confirm(`Remove ALL ${demoCount} demo articles? Real articles are not affected.`)) return;
    setDemoBusy(true);
    try {
      const { data } = await api.delete("/admin/demo/purge");
      toast.success(`Removed ${data.deleted} demo articles.`);
      await load();
    } catch (e) {
      toast.error("Could not remove demo articles: " + (e.response?.data?.detail || e.message));
    } finally { setDemoBusy(false); }
  };

  const toggle = async (a, key, label) => {
    try {
      await api.put(`/admin/articles/${a.id}`, { [key]: !a[key] });
      toast.success(label);
      load();
    } catch { toast.error("Update failed"); }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete article "${a.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/articles/${a.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const [regenId, setRegenId] = useState(null);
  const regen = async (a) => {
    setRegenId(a.id);
    try {
      await api.post("/admin/pdf-generator/generate", { article_id: a.id, publish: false });
      toast.success("PDF regenerated. Use the 'Open' link in the PDF column to download it.");
      load();
    } catch (e) {
      toast.error("Regenerate failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setRegenId(null);
    }
  };

  return (
    <div data-testid="admin-articles">
      <div className="flex items-center justify-between mb-3">
        <h1 className="font-serif text-3xl text-[#001F3F]">Articles</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {demoCount > 0 ? (
            <button onClick={purgeDemo} disabled={demoBusy} className="border border-red-300 text-red-600 px-4 py-3 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-red-50 disabled:opacity-50" data-testid="purge-demo-btn">
              <Trash2 className="w-4 h-4 inline mr-1" /> Remove demo ({demoCount})
            </button>
          ) : (
            <button onClick={seedDemo} disabled={demoBusy} className="border border-[#2BA6A6] text-[#2BA6A6] px-4 py-3 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#2BA6A6] hover:text-white disabled:opacity-50" data-testid="seed-demo-btn">
              {demoBusy ? "Loading…" : "Load sample articles"}
            </button>
          )}
          <Link to="/admin/pdf-generator" className="bg-[#001F3F] text-white px-5 py-3 text-xs uppercase tracking-[0.15em] font-semibold hover:bg-[#2BA6A6] transition-colors flex items-center gap-2" data-testid="new-article-btn">
            <Plus className="w-4 h-4" /> New Article (via PDF Generator)
          </Link>
        </div>
      </div>
      <div className="w-10 h-[2px] bg-[#2BA6A6] mb-6"></div>

      <div className="bg-white border border-gray-200">
        <table className="w-full text-sm font-sans">
          <thead className="bg-[#F5F7FA] text-[11px] uppercase tracking-wider text-[#001F3F]">
            <tr>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Vol/Issue</th>
              <th className="text-left px-4 py-3">Year</th>
              <th className="text-left px-4 py-3">PDF</th>
              <th className="text-left px-4 py-3">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-gray-500">No articles yet. Use the Official PDF Generator to create the first one.</td></tr>
            ) : list.map((a) => (
              <tr key={a.id} className="border-t border-gray-100">
                <td className="px-4 py-3 max-w-md">
                  <Link to={`/admin/pdf-generator?id=${a.id}`} className="text-[#001F3F] hover:text-[#2BA6A6] font-serif">{a.title}</Link>
                  <div className="text-[11px] text-gray-500 mt-0.5">/articles/{a.slug}</div>
                </td>
                <td className="px-4 py-3 text-xs uppercase tracking-wider text-[#2BA6A6]">{a.article_type}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{a.volume}/{a.issue}</td>
                <td className="px-4 py-3 text-xs text-gray-600">{a.year}</td>
                <td className="px-4 py-3">
                  {a.pdf_url ? <a href={assetUrl(a.pdf_url)} target="_blank" rel="noreferrer" className="text-[#2BA6A6] text-xs flex items-center gap-1"><FileText className="w-3 h-3" /> Open</a> : <span className="text-xs text-gray-400">—</span>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 items-center">
                    {a.published ? <span className="text-[10px] uppercase px-2 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200">Published</span> :
                      <span className="text-[10px] uppercase px-2 py-1 bg-gray-50 text-gray-600 border border-gray-200">Draft</span>}
                    {a.featured && <Star className="w-3 h-3 text-amber-400" />}
                    {a.hidden && <EyeOff className="w-3 h-3 text-red-500" />}
                    {a.is_demo && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-purple-50 text-purple-700 border border-purple-200">demo</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Link to={`/admin/pdf-generator?id=${a.id}`} className="text-xs text-[#2BA6A6] hover:text-[#001F3F] uppercase tracking-wider mr-3" data-testid={`edit-${a.id}`}><Edit3 className="w-3 h-3 inline" /> Edit</Link>
                  <button onClick={() => regen(a)} disabled={regenId === a.id} className="text-xs text-[#001F3F] hover:text-[#2BA6A6] uppercase tracking-wider mr-3 disabled:opacity-50" data-testid={`regen-pdf-${a.id}`} title="Regenerate the official PDF from the saved article data (fixes lost/broken PDF downloads)"><RefreshCw className={`w-3 h-3 inline ${regenId === a.id ? "animate-spin" : ""}`} /> {regenId === a.id ? "Generating…" : "Regen PDF"}</button>
                  <button onClick={() => toggle(a, "published", a.published ? "Unpublished" : "Published")} className="text-xs text-[#001F3F] hover:text-[#2BA6A6] uppercase tracking-wider mr-3">{a.published ? "Unpublish" : "Publish"}</button>
                  <button onClick={() => toggle(a, "featured", a.featured ? "Unfeatured" : "Featured")} className="text-xs text-[#001F3F] hover:text-[#2BA6A6] uppercase tracking-wider mr-3">{a.featured ? "Unfeature" : "Feature"}</button>
                  <button onClick={() => toggle(a, "hidden", a.hidden ? "Visible" : "Hidden")} className="text-xs text-[#001F3F] hover:text-[#2BA6A6] uppercase tracking-wider mr-3"><Eye className="w-3 h-3 inline" /> {a.hidden ? "Show" : "Hide"}</button>
                  <button onClick={() => del(a)} className="text-xs text-red-600 hover:text-red-800 uppercase tracking-wider" data-testid={`delete-article-${a.id}`}><Trash2 className="w-3 h-3 inline" /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
